import { cn } from '@/lib/cn';

/**
 * Champ : un champ du formulaire, avec son libelle au-dessus.
 *
 * Source : spec-design.md v2 section « Champ de formulaire ». Fond `fond-eleve`, bordure
 * `trait-fin` `bordure`, rayon `champ`, hauteur 52 pixels, retrait horizontal 16 pixels, texte 17
 * pixels. Le libelle se place AU-DESSUS du champ, en Inter 500, jamais a l'interieur.
 *
 * POURQUOI IL EXISTE ALORS QUE LE FORMULAIRE EST LE JALON SUIVANT. La spec le decrit, la page
 * `/systeme` doit montrer les composants dans toutes leurs variantes, et `J-03` construira ses six
 * champs avec ce composant plutot qu'en le redessinant. Sur la page du site, il n'est utilise que
 * par la section 19, qui reste sans ses champs jusqu'a `J-03` : ce composant n'y apparait donc pas
 * encore.
 *
 * LE LIBELLE N'EST JAMAIS DANS LE CHAMP, et ce n'est pas un choix d'esthetique : un libelle pose en
 * `placeholder` disparait des que l'utilisateur tape, et l'utilisateur qui revient sur un champ
 * rempli ne sait plus ce qu'on lui demandait.
 *
 * IL NE PORTE AUCUN TEXTE. Le libelle et le message d'erreur viennent de content/contenu.ts.
 */
export type ProprietesDeChamp = {
  readonly identifiant: string;
  readonly libelle: string;
  readonly type?: string;
  readonly obligatoire?: boolean;
  /** Le message affiche sous le champ. Sa presence met le champ en etat d erreur. */
  readonly erreur?: string;
  /**
   * Ce que `react-hook-form` rend pour un champ : son nom, ses deux gestionnaires, sa reference.
   *
   * AJOUTE LE 2026-08-14, TACHE 03-01. Ce composant existait depuis le sprint 04 et n'avait jamais
   * ete branche : il rendait un champ qui n'envoyait rien. La propriete est typee ici en proprietes
   * d'`input` plutot qu'avec le type de la bibliotheque, pour que ce fichier n'importe rien d'elle :
   * il reste rendu cote serveur partout ailleurs.
   */
  readonly enregistrement?: React.ComponentPropsWithRef<'input'>;
  /**
   * Les valeurs d'un choix ferme. Quand elle est fournie, le champ se rend en liste deroulante.
   *
   * AJOUTEE LE 2026-08-20 POUR LA VILLE DE PREFERENCE, sur la demande de Wahid : « rendre la ville
   * de preference et la ville une liste deroulante avec les plus grandes villes du Maroc ». Ecrire
   * un second composant `Liste` aurait duplique le libelle, le message d'erreur et les six classes
   * de la spec de design : les deux formes de saisie ne different que par la balise.
   */
  readonly choix?: readonly string[];
  /** Ce que la premiere entree de la liste affiche tant que rien n'est choisi. */
  readonly entreeVide?: string;
};

export default function Champ({
  identifiant,
  libelle,
  type = 'text',
  obligatoire = false,
  erreur,
  enregistrement,
  choix,
  entreeVide,
}: ProprietesDeChamp) {
  /*
   * LES CLASSES SONT LES MEMES POUR LES DEUX BALISES, et c'est le propos : `spec-design.md` v2
   * § « Champ de formulaire » decrit UNE apparence, pas une par balise. Seul `pr-10` s'ajoute a la
   * liste, pour que le texte le plus long ne passe pas sous la fleche que le navigateur y dessine.
   */
  const apparence = cn(
    'h-13 w-full rounded-champ border bg-fond-eleve px-4',
    'font-corps text-corps text-texte-courant',
    'transition-colors duration-150 ease-douce',
    'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
    erreur ? 'border-action-survol' : 'border-bordure focus:border-action',
  );

  const communes = {
    id: identifiant,
    name: identifiant,
    required: obligatoire,
    'aria-invalid': erreur ? (true as const) : undefined,
    'aria-describedby': erreur ? `${identifiant}-erreur` : undefined,
  };

  return (
    <div data-composant="Champ" className="flex flex-col gap-2">
      <label htmlFor={identifiant} className="font-corps font-medium text-corps text-texte-courant">
        {libelle}
      </label>
      {choix ? (
        <select
          {...communes}
          /*
           * `react-hook-form` rend les memes quatre proprietes pour un `select` que pour un `input`,
           * a la reference pres. Le type est celui d'un `input` parce que ce fichier n'importe rien
           * de la bibliotheque, et la conversion ne porte donc que sur le type de cette reference.
           */
          {...(enregistrement as unknown as React.ComponentPropsWithRef<'select'>)}
          className={cn(apparence, 'pr-10')}
        >
          <option value="">{entreeVide}</option>
          {choix.map((valeur) => (
            <option key={valeur} value={valeur}>
              {valeur}
            </option>
          ))}
        </select>
      ) : (
        <input {...communes} type={type} {...enregistrement} className={apparence} />
      )}
      {erreur ? (
        <p id={`${identifiant}-erreur`} className="font-corps text-legende text-action-survol">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}
